import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SatisfactionSurvey } from '../shared/models/satisfaction-survey.model';

@Component({
  selector: 'app-satisfaction-survey-duplicate',
  templateUrl: './satisfaction-survey-duplicate.component.html',
  styleUrls: ['./satisfaction-survey-duplicate.component.scss'],
})
export class SatisfactionSurveyDuplicateComponent implements OnInit, OnDestroy {
  @Input() survey: SatisfactionSurvey;
  @Input() surveyNamesBySiteId: { [key: number]: string[] };
  @Output() cancelDialog = new EventEmitter<any>();
  @Output() hideDialog = new EventEmitter<any>();

  @ViewChild('surveyFormTemplateRef', { static: true }) surveyFormTemplateRef: TemplateRef<any>;
  form: FormGroup = this.formBuilder.group({
    selectedSiteId: [null, Validators.required],
    surveyName: ['', Validators.required],
  });

  surveySites: Choice[] = [];
  showNameDuplicateWarning: boolean;
  subscriptions = new Subscription();
  checkSurveysNameSubscriptions = new Subscription();
  warningNameAlreadyExists = false;

  constructor(
    @Inject('DropdownItemsFactory') private dropdownItemsFactory: any,
    @Inject('sites') private sites: SiteV2[],
    @Inject('sitesMap') private sitesMap: Map<number, SiteV2>,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private basicService: BasicService
  ) {}

  ngOnInit(): void {
    this.form.get('selectedSiteId').valueChanges.subscribe(() => this.form.get('surveyName').updateValueAndValidity());

    const { site_id, name } = this.survey;
    const surveyType = this.sitesMap.get(site_id).type;
    const sitesFiltered = this.sites.filter(site => site.type === surveyType);
    this.surveySites = this.dropdownItemsFactory.buildSiteItems(sitesFiltered);

    this.form.patchValue({
      selectedSiteId: site_id,
      surveyName: `${this.basicService.translate('satisfaction-surveys/copy/copy-of')} ${name}`,
    });

    this.uniqueNamePerAccountValidator(this.form.get('selectedSiteId'), this.surveyNamesBySiteId);

    this.checkSurveysNameSubscriptions?.unsubscribe();
    this.checkSurveysNameSubscriptions = this.form.valueChanges.subscribe(() => {
      this.uniqueNamePerAccountValidator(this.form.get('selectedSiteId'), this.surveyNamesBySiteId);
    });

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['medium-size'],
      data: {
        title: this.basicService.translate('satisfaction-surveys/copy/title'),
        description: this.basicService.translate('satisfaction-surveys/copy/info-msg'),
        confirmBtnText: this.basicService.translate('save'),
        cancelBtnText: this.basicService.translate('cancel'),
        surveySites: this.surveySites,
        selectedSiteId: this.survey.site_id,
        templateRef: this.surveyFormTemplateRef,
        disable: () => this.form.invalid,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hideDialog.emit(this.form.value);
      } else {
        this.cancelDialog.emit();
      }
    });
  }

  private uniqueNamePerAccountValidator(siteCtrl, namesBySiteId: { [key: number]: string[] }) {
    const namesForSelectedSite: string[] = namesBySiteId[siteCtrl.value];
    this.warningNameAlreadyExists = namesForSelectedSite && namesForSelectedSite.includes(this.form.value.surveyName);
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
